'use client';
import { useEffect, useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { useT } from '../../i18n';

const HeroSection = () => {
  const t = useT();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  // Hidden shortcut: press Ctrl 5 times within 2 seconds to jump to /xyz
  useEffect(() => {
    let ctrlCount = 0;
    let lastCtrlTime = 0;
    const resetDelay = 2000;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control') {
        const now = Date.now();
        if (now - lastCtrlTime > resetDelay) {
          ctrlCount = 0;
        }
        ctrlCount += 1;
        lastCtrlTime = now;

        if (ctrlCount >= 5) {
          window.location.href = '/xyz';
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-jkd-black">
      {/* Background Image - Bruce Lee cinematic */}
      <div className="absolute inset-0 z-0">
        <img
          src="/images/brucelee-8.jpg"
          alt="Bruce Lee"
          className="w-full h-full object-cover object-top opacity-60 scale-105"
        />
        {/* Dark cinematic overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-jkd-black/80 via-jkd-black/60 to-jkd-black" />
        <div className="absolute inset-0 bg-gradient-to-r from-jkd-black/70 via-transparent to-jkd-black/70" />
      </div>

      {/* Water ripple effect overlay */}
      <div className="absolute inset-0 z-[1] opacity-20 pointer-events-none">
        <div className="absolute inset-0 water-ripple" />
      </div>

      {/* Subtle grain texture */}
      <div className="absolute inset-0 z-[2] opacity-[0.03] pointer-events-none noise-bg" />

      {/* Main Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-7 lg:px-8 text-center">
        {/* Philosophy kicker */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-2 mb-8 border border-jkd-gold/30 rounded-full bg-jkd-black/50 backdrop-blur-sm transition-all duration-1000 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-jkd-gold animate-pulse" />
          <span className="text-jkd-gold text-sm font-medium tracking-wider uppercase">
            {t('hero.kicker')}
          </span>
        </div>

        {/* Main Headline */}
        <h1
          className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-6 tracking-tight leading-[1.1] transition-all duration-1000 delay-200 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="block text-white mb-2">
            {t('hero.titleA')}
          </span>
          <span className="block text-jkd-gold text-shadow-gold">
            {t('hero.titleB')}
          </span>
        </h1>

        {/* Subtitle */}
        <p
          className={`text-lg sm:text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed transition-all duration-1000 delay-400 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {t('hero.subtitle')}
        </p>

        {/* CTA Buttons */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 transition-all duration-1000 delay-600 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <a
            href="/diagnosis"
            className="group btn-gold w-full sm:w-auto text-lg px-8 py-4 inline-flex items-center justify-center gap-2"
          >
            {t('hero.cta')}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
          <button
            onClick={scrollToServices}
            className="btn-outline w-full sm:w-auto text-lg px-8 py-4"
          >
            {t('hero.approach')}
          </button>
        </div>

        {/* Bruce Lee quote */}
        <blockquote
          className={`relative max-w-2xl mx-auto transition-all duration-1000 delay-800 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-jkd-gold text-6xl font-serif opacity-30">"</div>
          <p className="text-xl md:text-2xl text-white/90 font-light italic leading-relaxed pt-4">
            {t('hero.quote')}
          </p>
          <footer className="mt-4 text-jkd-gold text-sm tracking-widest uppercase">
            — Bruce Lee
          </footer>
        </blockquote>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToServices}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-jkd-gold/70 hover:text-jkd-gold transition-colors animate-float"
        aria-label="Scroll down"
      >
        <ChevronDown className="w-8 h-8" />
      </button>

      {/* Decorative water line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-jkd-gold/50 to-transparent z-10" />

      <style>{`
        .water-ripple {
          background: 
            radial-gradient(ellipse at 30% 50%, rgba(232, 255, 0, 0.08) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 50%, rgba(232, 255, 0, 0.05) 0%, transparent 50%);
          animation: rippleMove 8s ease-in-out infinite;
          filter: blur(60px);
        }
        @keyframes rippleMove {
          0%, 100% { transform: translateX(-5%) scale(1); opacity: 0.5; }
          50% { transform: translateX(5%) scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
